import { banco } from '../db/banco'
import type { Produto, SubPedido } from '../db/tipos'
import { gravarLocalEEnfileirar } from '../sync/outbox'
import { resolverImposto } from '../impostos/resolverImposto'

// ▶ O imposto INFORMATIVO de um item (IPI e ICMS ST que o cliente vai pagar).
// Le a condicao de pagamento (desconto_pct) e o cliente (CEP) so quando a
// representada calcula imposto -- senao e trabalho a toa em todo pedido de toda
// representada comum. Devolve null quando nao calcula ("vende como hoje").
export async function comporImpostoInformativo(
  produto: Produto,
  clienteId: string,
  precoSemImposto: number,
  freteTipo: SubPedido['frete_tipo'],
  condicaoPagamentoId: string | null,
): Promise<Awaited<ReturnType<typeof resolverImposto>>> {
  const representada = await banco.representadas.get(produto.representada_id)
  if (!representada?.calcula_imposto) return null
  const cliente = await banco.clientes.get(clienteId)
  const condicao = condicaoPagamentoId
    ? await banco.condicoes_pagamento.get(condicaoPagamentoId)
    : undefined
  const descontoFrete = freteTipo
    ? await banco.descontos_frete
        .where('[representada_id+regiao_destino+incoterm]')
        // regiao_destino resolvida a partir da UF do cliente -- ver nota abaixo
        .equals([produto.representada_id, cliente?.regiao ?? '', freteTipo])
        .filter((d) => d.apagado_em === null)
        .first()
    : undefined
  return resolverImposto({
    produtoId: produto.id,
    representadaId: produto.representada_id,
    precoSemImposto,
    cep: cliente?.cep ?? null,
    freteTipo,
    descontoFretePct: descontoFrete?.desconto_pct ?? 0,
    descontoPrazoPct: condicao?.desconto_pct ?? 0,
  })
}

// ▶ Pedido dela 14/10: frete e condicao de pagamento sao perguntados SO no
// fechamento (antes eram no primeiro item, e o app perguntava 2x). Como o
// imposto e informativo, o item nasce com a composicao sem frete/prazo e, quando
// o fechamento grava a escolha, a composicao e REFEITA com ela. O preco de
// venda nao muda -- so os valores de IPI/ICMS ST mostrados ao cliente.
export async function recalcularImpostoDoSubPedido(subPedidoId: string): Promise<void> {
  const subPedido = await banco.sub_pedidos.get(subPedidoId)
  if (!subPedido) return
  const pedido = await banco.pedidos.get(subPedido.pedido_id)
  if (!pedido) return
  const itens = await banco.itens_pedido
    .where('sub_pedido_id').equals(subPedidoId).filter((i) => i.apagado_em === null).toArray()
  for (const item of itens) {
    const produto = await banco.produtos.get(item.produto_id)
    if (!produto) continue
    const composicao = await comporImpostoInformativo(
      produto, pedido.cliente_id, item.preco_unitario,
      subPedido.frete_tipo, subPedido.condicao_pagamento_id,
    )
    const novo = {
      imposto_origem: composicao?.origem ?? null,
      imposto_destino_icms: composicao?.destinoIcms ?? null,
      imposto_diferenca_icms: composicao?.diferencaIcms ?? null,
      imposto_ipi: composicao?.ipi ?? null,
    }
    const igual = novo.imposto_ipi === (item.imposto_ipi ?? null)
      && novo.imposto_diferenca_icms === (item.imposto_diferenca_icms ?? null)
      && novo.imposto_origem === (item.imposto_origem ?? null)
    if (!igual) await gravarLocalEEnfileirar('itens_pedido', { ...item, ...novo })
  }
}
